// Per-job document library — replaces the old "Coming soon" attachment
// stubs in JobDetailPage Card 6. Office staff upload PDF/DOCX/XLSX/images;
// crew upload via Telegram (same backend). Flat list, optional free-text
// label per doc. PDF + images preview inline; docx/xlsx download-only.
//
// Self-contained: owns its query, upload (multi-file), optimistic delete,
// preview modal, hash deep-link (#doc=<id>) and Escape handling — so the
// host page just mounts <JobDocumentsSection jobId={id} />.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UploadCloud,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  File as FileIcon,
  Download,
  Eye,
  Trash2,
  Loader2,
  X,
} from 'lucide-react';
import type { JobDocument } from '@gutterinv/shared';
import {
  getJobDocuments,
  uploadJobDocument,
  deleteJobDocument,
  getJobDocumentUrl,
} from '../../api/client.js';
import { qk } from '../../lib/queryKeys.js';
import { formatDate } from '../../lib/format.js';
import { Input } from '../ui/input.js';
import { Badge } from '../ui/badge.js';
import { ConfirmDialog } from '../ConfirmDialog.js';
import { useEscapeKey } from '../../hooks/useEscapeKey.js';

// Mirror of the server cap (apps/worker/src/lib/doc-validation.ts).
const MAX_DOC_BYTES = 20 * 1024 * 1024;
const ACCEPT =
  '.pdf,.docx,.xlsx,.jpg,.jpeg,.png,.heic,' +
  'application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'image/jpeg,image/png,image/heic';

const PREVIEWABLE = new Set(['application/pdf', 'image/jpeg', 'image/png']);

function iconFor(mime: string) {
  if (mime === 'application/pdf') return FileText;
  if (mime.includes('spreadsheetml')) return FileSpreadsheet;
  if (mime.includes('wordprocessingml')) return FileText;
  if (mime.startsWith('image/')) return ImageIcon;
  return FileIcon;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

interface PendingUpload {
  tempId: string;
  name: string;
  error?: string;
}

export function JobDocumentsSection({ jobId }: { jobId: string }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState('');
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<JobDocument | null>(null);
  const [preview, setPreview] = useState<JobDocument | null>(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: qk.jobs.documents(jobId),
    queryFn: () => getJobDocuments(jobId),
  });

  // --- Upload (one POST per file; per-file pending/error rows so one
  //     rejection doesn't kill the batch). Invalidate-on-success matches
  //     the codebase norm for creates. ---
  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      const useLabel = label.trim() || undefined;
      for (const file of list) {
        const tempId = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
        if (file.size > MAX_DOC_BYTES) {
          setPending((p) => [
            ...p,
            { tempId, name: file.name, error: 'Too large (max 20 MB)' },
          ]);
          continue;
        }
        setPending((p) => [...p, { tempId, name: file.name }]);
        uploadJobDocument(jobId, file, useLabel)
          .then(() => {
            setPending((p) => p.filter((u) => u.tempId !== tempId));
            queryClient.invalidateQueries({ queryKey: qk.jobs.documents(jobId) });
          })
          .catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : 'Upload failed';
            setPending((p) =>
              p.map((u) => (u.tempId === tempId ? { ...u, error: msg } : u)),
            );
          });
      }
      setLabel('');
    },
    [jobId, label, queryClient],
  );

  // --- Delete (true optimistic remove + rollback; deletes feel laggy
  //     otherwise. Deviates from the page's invalidate-on-success norm
  //     deliberately — documented here.) ---
  const deleteMutation = useMutation({
    mutationFn: (docId: string) => deleteJobDocument(jobId, docId),
    onMutate: async (docId: string) => {
      await queryClient.cancelQueries({ queryKey: qk.jobs.documents(jobId) });
      const prev = queryClient.getQueryData<JobDocument[]>(qk.jobs.documents(jobId));
      queryClient.setQueryData<JobDocument[]>(qk.jobs.documents(jobId), (old) =>
        (old ?? []).filter((d) => d.id !== docId),
      );
      return { prev };
    },
    onError: (_err, _docId, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(qk.jobs.documents(jobId), ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.jobs.documents(jobId) });
    },
  });

  // --- Hash deep-link: refresh re-opens an open preview (#doc=<id>).
  //     Distinct from the page's #phases anchor. ---
  useEffect(() => {
    const m = /^#doc=([0-9a-f-]+)$/i.exec(window.location.hash);
    if (m && docs.length) {
      const found = docs.find((d) => d.id === m[1]);
      if (found && PREVIEWABLE.has(found.mimeType)) setPreview(found);
    }
    // run once docs first arrive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs.length]);

  const openPreview = (doc: JobDocument) => {
    setPreview(doc);
    window.history.replaceState(null, '', `#doc=${doc.id}`);
  };
  const closePreview = useCallback(() => {
    setPreview(null);
    if (window.location.hash.startsWith('#doc=')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);
  useEscapeKey(preview !== null, closePreview);

  return (
    <>
      {/* Upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
        }}
        className={
          'rounded-md border border-dashed px-3 py-4 text-center transition-colors ' +
          (dragging ? 'border-primary bg-secondary/30' : 'border-border')
        }
      >
        <UploadCloud className="mx-auto h-5 w-5 text-taupe" />
        <p className="mt-1 text-xs text-muted-foreground">
          Drag files here, or{' '}
          <button
            type="button"
            className="font-medium text-primary underline-offset-2 hover:underline"
            onClick={() => fileInputRef.current?.click()}
          >
            choose files
          </button>
        </p>
        <p className="mt-0.5 text-[0.625rem] text-taupe">
          PDF, Word, Excel, or images · up to 20 MB each
        </p>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Optional label (applies to the next upload)"
          className="mt-2 h-8 text-xs"
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {/* List */}
      <div className="mt-3 space-y-2">
        {isLoading && (
          <div className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}

        {/* In-flight uploads */}
        {pending.map((u) => (
          <div
            key={u.tempId}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
          >
            {u.error ? (
              <X className="h-3.5 w-3.5 shrink-0 text-destructive" />
            ) : (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-taupe" />
            )}
            <span className="truncate text-xs text-foreground">{u.name}</span>
            {u.error ? (
              <span className="ml-auto text-[0.625rem] text-destructive">{u.error}</span>
            ) : (
              <span className="ml-auto text-[0.625rem] text-taupe">Uploading…</span>
            )}
            {u.error && (
              <button
                onClick={() => setPending((p) => p.filter((x) => x.tempId !== u.tempId))}
                className="text-taupe hover:text-foreground"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}

        {!isLoading && docs.length === 0 && pending.length === 0 && (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-taupe">
            No documents yet.
          </div>
        )}

        {docs.map((doc) => {
          const Icon = iconFor(doc.mimeType);
          const canPreview = PREVIEWABLE.has(doc.mimeType);
          return (
            <div
              key={doc.id}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
            >
              <Icon className="h-4 w-4 shrink-0 text-taupe" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-xs font-medium text-foreground">
                    {doc.fileName}
                  </span>
                  {doc.label && (
                    <Badge variant="secondary" className="shrink-0 text-[0.625rem]">
                      {doc.label}
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 truncate text-[0.625rem] text-taupe">
                  {formatBytes(doc.sizeBytes)} · {doc.uploadedByName ?? 'Unknown'}
                  {doc.source === 'telegram' ? ' (crew)' : ''} · {formatDate(doc.createdAt)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {canPreview && (
                  <button
                    onClick={() => openPreview(doc)}
                    className="rounded p-1 text-taupe hover:bg-secondary hover:text-foreground"
                    aria-label="Preview"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                )}
                <a
                  href={getJobDocumentUrl(jobId, doc.id)}
                  download={doc.fileName}
                  className="rounded p-1 text-taupe hover:bg-secondary hover:text-foreground"
                  aria-label="Download"
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
                <button
                  onClick={() => setDeleteTarget(doc)}
                  className="rounded p-1 text-taupe hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete document?"
        description={
          deleteTarget ? `"${deleteTarget.fileName}" will be permanently removed.` : ''
        }
        confirmLabel="Delete"
        variant="destructive"
        isPending={deleteMutation.isPending}
        error={deleteMutation.error instanceof Error ? deleteMutation.error.message : null}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Preview modal (pdf via iframe; images centered) */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePreview();
          }}
        >
          <div className="relative flex h-full w-full max-w-5xl flex-col">
            <div className="mb-2 flex items-center gap-2">
              <span className="truncate text-sm font-medium text-white">{preview.fileName}</span>
              <a
                href={getJobDocumentUrl(jobId, preview.id)}
                download={preview.fileName}
                className="ml-auto rounded-full bg-white/90 p-2 text-foreground shadow-lg hover:bg-white"
                aria-label="Download"
              >
                <Download className="h-4 w-4" />
              </a>
              <button
                onClick={closePreview}
                className="rounded-full bg-white/90 p-2 text-foreground shadow-lg hover:bg-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {preview.mimeType === 'application/pdf' ? (
              <iframe
                src={getJobDocumentUrl(jobId, preview.id)}
                title={preview.fileName}
                className="h-full w-full rounded-lg border-0 bg-white"
              />
            ) : (
              <div className="flex flex-1 items-center justify-center overflow-auto">
                <img
                  src={getJobDocumentUrl(jobId, preview.id)}
                  alt={preview.fileName}
                  className="max-h-full max-w-full rounded-lg shadow-2xl"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
